/**
 * Los iconos de la aplicación. UN SOLO JUEGO Y UN SOLO SITIO.
 *
 * Son Tabler (`@tabler/icons-react`), y aquí se les ponen NUESTROS valores por
 * defecto. El resto del código importa siempre de este archivo y nunca del
 * paquete: eso es lo que hace que cambiar de juego de iconos, o subir el
 * grosor del trazo, sea tocar un archivo y no ciento.
 *
 * Dos juegos conviviendo son dos lenguajes visuales --trazos, remates y
 * rejilla distintos-- y se nota en cuanto dos iconos quedan cerca. Por eso no
 * queda ni uno de `lucide-react`, tampoco en las primitivas que vinieron de
 * shadcn: son nuestras desde que se copiaron al repositorio.
 *
 * Se importa por nombre, nunca el paquete entero (`import * as`): Tabler son
 * miles de iconos y solo deben acabar en el bundle los que se usan.
 */
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowForwardUp,
  IconArrowLeft,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconArrowsSplit,
  IconArrowUp,
  IconBan,
  IconBarcode,
  IconBolt,
  IconCalendar,
  IconChartHistogram,
  IconCheck,
  IconChecklist,
  IconChecks,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCircle,
  IconCircleCheck,
  IconCircleDot,
  IconClock,
  IconCoins,
  IconCommand,
  IconDots,
  IconDownload,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFile,
  IconFileInvoice,
  IconFileText,
  IconFlag,
  IconFocusCentered,
  IconHelp,
  IconInbox,
  IconKeyboard,
  IconLayoutDashboard,
  IconLayoutSidebar,
  IconLayoutSidebarLeftCollapse,
  IconLifebuoy,
  IconLoader2,
  IconLock,
  IconLogin,
  IconLogout,
  IconMessage,
  IconMicrophone,
  IconMicrophoneOff,
  IconPaperclip,
  IconPencil,
  IconPhoto,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSelector,
  IconSend,
  IconSettings,
  IconSparkles,
  IconSquare,
  IconTable,
  IconTarget,
  IconTargetArrow,
  IconTrash,
  IconTruck,
  IconUpload,
  IconUser,
  IconUserCheck,
  IconUsers,
  IconX,
  IconZoomIn,
  IconZoomOut,
  type IconProps,
} from "@tabler/icons-react";
import { createElement, type FunctionComponent } from "react";

/**
 * A qué tamaño y con qué trazo se dibujan.
 *
 * 16px es el tamaño que ya asumen los botones (`size-4`). El tamaño va como
 * atributo y no como clase A PROPÓSITO: así una clase `size-*` --la que el
 * botón pone según su tamaño, o la que ponga quien coloque el icono-- sigue
 * mandando sobre esto, que es solo el valor de partida.
 */
const SIZE = 16;
const STROKE = 2;

type IconComponent = FunctionComponent<IconProps>;

const withDefaults = (Icon: IconComponent, name: string): IconComponent => {
  const Styled: IconComponent = (props) =>
    createElement(Icon, { size: SIZE, stroke: STROKE, ...props });
  Styled.displayName = name;
  return Styled;
};

/* Navegación y estructura. */
export const ArrowLeftIcon = withDefaults(IconArrowLeft, "ArrowLeftIcon");
export const ArrowUpIcon = withDefaults(IconArrowUp, "ArrowUpIcon");
export const ArrowDownIcon = withDefaults(IconArrowDown, "ArrowDownIcon");
export const ChevronLeftIcon = withDefaults(IconChevronLeft, "ChevronLeftIcon");
export const ChevronRightIcon = withDefaults(
  IconChevronRight,
  "ChevronRightIcon",
);
export const ChevronDownIcon = withDefaults(IconChevronDown, "ChevronDownIcon");
export const ChevronUpIcon = withDefaults(IconChevronUp, "ChevronUpIcon");
/** El de ordenar una columna que todavía no ordena por nada. */
export const SortIcon = withDefaults(IconSelector, "SortIcon");
export const SidebarShowIcon = withDefaults(
  IconLayoutSidebar,
  "SidebarShowIcon",
);
export const SidebarHideIcon = withDefaults(
  IconLayoutSidebarLeftCollapse,
  "SidebarHideIcon",
);

/* Acciones. */
export const PlusIcon = withDefaults(IconPlus, "PlusIcon");
export const CloseIcon = withDefaults(IconX, "CloseIcon");
export const CheckIcon = withDefaults(IconCheck, "CheckIcon");
/** Confirmar lo propuesto, una o en lote. */
export const ConfirmIcon = withDefaults(IconChecks, "ConfirmIcon");
export const AttachIcon = withDefaults(IconPaperclip, "AttachIcon");
export const DownloadIcon = withDefaults(IconDownload, "DownloadIcon");
/** Detener el turno: un cuadrado, como en cualquier reproductor. */
export const StopIcon = withDefaults(IconSquare, "StopIcon");
export const SettingsIcon = withDefaults(IconSettings, "SettingsIcon");

/* Los dos modos de trabajo (§9.2). */
export const ChatIcon = withDefaults(IconMessage, "ChatIcon");
export const WorkIcon = withDefaults(IconLayoutDashboard, "WorkIcon");

/* Las secciones del panel. */

/* El proceso de revisión de facturas: la sección y su diagrama. */
/** Los cuatro tipos de paso: Automático, Con IA, Manual y Envío. */
export const RuleStepIcon = withDefaults(IconBolt, "RuleStepIcon");
export const AiStepIcon = withDefaults(IconSparkles, "AiStepIcon");
export const HumanStepIcon = withDefaults(IconUserCheck, "HumanStepIcon");
export const EffectStepIcon = withDefaults(IconSend, "EffectStepIcon");
/** Lo que todavía no existe: «Próximamente». */
export const PlannedIcon = withDefaults(IconClock, "PlannedIcon");
/** Las validaciones en paralelo: se abren en ramas y se vuelven a juntar. */
export const ParallelIcon = withDefaults(IconArrowsSplit, "ParallelIcon");
export const EndIcon = withDefaults(IconFlag, "EndIcon");
export const ZoomInIcon = withDefaults(IconZoomIn, "ZoomInIcon");
export const ZoomOutIcon = withDefaults(IconZoomOut, "ZoomOutIcon");
/** Volver a encuadrar todo el dibujo. */
export const FitViewIcon = withDefaults(IconFocusCentered, "FitViewIcon");

/* Las facturas en revisión: la sección, su foto y su seguimiento. */
export const DocumentsIcon = withDefaults(IconFileInvoice, "DocumentsIcon");
export const SearchIcon = withDefaults(IconSearch, "SearchIcon");
/** Abrir algo fuera de la aplicación, en otra pestaña. */
export const ExternalLinkIcon = withDefaults(
  IconExternalLink,
  "ExternalLinkIcon",
);

/* La digitación: la sección, la pantalla completa y la ayuda del teclado. */
export const DataEntryIcon = withDefaults(IconKeyboard, "DataEntryIcon");
/** «Acierto»: cuánto acierta lo que propone el sistema. */
export const AccuracyIcon = withDefaults(IconTarget, "AccuracyIcon");
export const FullscreenIcon = withDefaults(
  IconArrowsMaximize,
  "FullscreenIcon",
);
export const ExitFullscreenIcon = withDefaults(
  IconArrowsMinimize,
  "ExitFullscreenIcon",
);
/** La foto entera, tal como llegó. */
export const PhotoIcon = withDefaults(IconPhoto, "PhotoIcon");
/** La chuleta de atajos. */
export const HelpIcon = withDefaults(IconHelp, "HelpIcon");
/** La paleta de comandos. */
export const CommandIcon = withDefaults(IconCommand, "CommandIcon");
/**
 * Alfred, el buzón de lo que se necesita: una idea, algo que falta o algo
 * que no funciona. El botón de la cabecera y su sección del menú.
 */
export const AlfredIcon = withDefaults(IconLifebuoy, "AlfredIcon");
/** Un adjunto que no es una imagen: no tiene miniatura. */
export const FileIcon = withDefaults(IconFile, "FileIcon");
/** Anular: no se borra, se deja sin efecto. */
export const CancelIcon = withDefaults(IconBan, "CancelIcon");
/** Volver al valor sugerido. */
export const UndoIcon = withDefaults(IconArrowBackUp, "UndoIcon");
/** Rehacer lo deshecho. */
export const RedoIcon = withDefaults(IconArrowForwardUp, "RedoIcon");
/** Los recuadros sobre la foto: a la vista u ocultos. */
export const BoxesShownIcon = withDefaults(IconEye, "BoxesShownIcon");
export const BoxesHiddenIcon = withDefaults(IconEyeOff, "BoxesHiddenIcon");

/* Alertas: la severidad se lee antes de leer el texto. */
export const WarningIcon = withDefaults(IconAlertTriangle, "WarningIcon");
/** Volver a intentar algo que no llegó al servidor. */
export const RetryIcon = withDefaults(IconRefresh, "RetryIcon");
export const InfoIcon = withDefaults(IconCircleDot, "InfoIcon");

/* Consumo de tokens y su coste estimado (2.4). */
export const UsageIcon = withDefaults(IconCoins, "UsageIcon");

/* Las 7 pantallas del admin viejo que se migran (B2): distribuidores,
 * participantes, periodos, SKU, objetivos, tareas y ajustes. */
export const ParticipantsIcon = withDefaults(IconUsers, "ParticipantsIcon");
export const DistributorsIcon = withDefaults(IconTruck, "DistributorsIcon");
export const PeriodsIcon = withDefaults(IconCalendar, "PeriodsIcon");
export const SkusIcon = withDefaults(IconBarcode, "SkusIcon");
export const ObjectivesIcon = withDefaults(IconTargetArrow, "ObjectivesIcon");
export const TasksIcon = withDefaults(IconChecklist, "TasksIcon");

/* Lo que se enseña cuando no hay nada que enseñar. */
export const EmptyIcon = withDefaults(IconInbox, "EmptyIcon");
/** Cuando no hay nada que mirar y eso es una buena noticia, no un hueco. */
export const OkIcon = withDefaults(IconCircleCheck, "OkIcon");

/* Los chequeos de la pre-decisión: pasa, no pasa, dudoso y no aplica. */
/* Dónde mirar en la digitación: lo leído con confianza baja o media. */
export const LowConfidenceIcon = withDefaults(
  IconAlertTriangle,
  "LowConfidenceIcon",
);
export const MediumConfidenceIcon = withDefaults(
  IconAlertCircle,
  "MediumConfidenceIcon",
);

/* Lo que anuncia a un componente del agente que no cabe donde está (§9). */
export const TableIcon = withDefaults(IconTable, "TableIcon");
export const ChartIcon = withDefaults(IconChartHistogram, "ChartIcon");
export const DocumentIcon = withDefaults(IconFileText, "DocumentIcon");
export const UploadedIcon = withDefaults(IconUpload, "UploadedIcon");

/* El menú de una fila del historial: renombrar y borrar. */
export const MoreIcon = withDefaults(IconDots, "MoreIcon");
export const RenameIcon = withDefaults(IconPencil, "RenameIcon");
export const DeleteIcon = withDefaults(IconTrash, "DeleteIcon");
/** La marca redonda de un radio: se rellena con `fill-current`. */
export const DotIcon = withDefaults(IconCircle, "DotIcon");

/* Dictado por voz. */
export const MicIcon = withDefaults(IconMicrophone, "MicIcon");
export const MicOffIcon = withDefaults(IconMicrophoneOff, "MicOffIcon");
/** El que gira mientras se transcribe. */
export const SpinnerIcon = withDefaults(IconLoader2, "SpinnerIcon");

/* La sesión. */
export const SignInIcon = withDefaults(IconLogin, "SignInIcon");
export const SignOutIcon = withDefaults(IconLogout, "SignOutIcon");
/** Quien está dentro, cuando no hay de dónde sacar sus iniciales. */
export const UserIcon = withDefaults(IconUser, "UserIcon");
/** Acceso denegado: la aplicación, o una acción. */
export const LockIcon = withDefaults(IconLock, "LockIcon");
